import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  FolderKanban, Users, Palmtree, Receipt, Clock, MessageSquare,
  AlertTriangle, TrendingUp, CalendarDays, Bell, ArrowRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

function AdminDashboard({ API, navigate }) {
  const [stats, setStats] = useState(null);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    Promise.all([axios.get(`${API}/dashboard/stats`), axios.get(`${API}/notifications`)])
      .then(([s, n]) => { setStats(s.data); setNotifs(n.data); }).catch(() => {});
  }, [API]);

  const kpis = [
    { label: 'Aktive Projekte', value: stats?.projects_count || 0, icon: FolderKanban, color: 'text-blue-400 bg-blue-400/10' },
    { label: 'Mitarbeiter', value: stats?.users_count || 0, icon: Users, color: 'text-emerald-400 bg-emerald-400/10' },
    { label: 'Offene Anträge', value: stats?.pending_vacations || 0, icon: Palmtree, color: 'text-amber-400 bg-amber-400/10' },
    { label: 'Gesamtumsatz', value: `${(stats?.revenue || 0).toLocaleString('de-DE')} EUR`, icon: TrendingUp, color: 'text-violet-400 bg-violet-400/10' },
    { label: 'Offene Posten', value: `${(stats?.open_total || 0).toLocaleString('de-DE')} EUR`, sub: `${stats?.open_invoices || 0} Rechnungen`, icon: Receipt, color: 'text-rose-400 bg-rose-400/10' },
    { label: 'Überfällig', value: stats?.overdue_invoices || 0, icon: AlertTriangle, color: stats?.overdue_invoices > 0 ? 'text-rose-400 bg-rose-400/10' : 'text-slate-400 bg-slate-400/10' },
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Dashboard</h1>
        {notifs.length > 0 && <div className="flex items-center gap-2"><Bell className="w-5 h-5 text-amber-400" /><Badge className="bg-rose-500 text-white rounded-full text-xs px-2">{notifs.length}</Badge></div>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((kpi, i) => (
          <div key={kpi.label} className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500">{kpi.label}</span>
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${kpi.color}`}><kpi.icon className="w-4 h-4" strokeWidth={1.5} /></div>
            </div>
            <p className="text-xl font-bold text-white font-mono">{kpi.value}</p>
            {kpi.sub && <p className="text-[10px] text-slate-500 mt-0.5">{kpi.sub}</p>}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Umsatz (letzte 6 Monate)</h2>
          {stats?.monthly_revenue?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.monthly_revenue}>
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '2px', color: '#f8fafc', fontSize: 12 }} formatter={(v) => [`${v.toLocaleString('de-DE')} EUR`, 'Umsatz']} />
                <Bar dataKey="revenue" fill="#f59e0b" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[220px] flex items-center justify-center text-slate-600 text-sm">Noch keine Umsatzdaten</div>}
        </div>
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2"><Bell className="w-4 h-4 text-amber-500" />Benachrichtigungen</h2>
          {notifs.length > 0 ? (
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {notifs.map((n, i) => (
                <div key={i} onClick={() => { if (n.type === 'overdue') navigate('/finanzen'); if (n.type === 'vacation') navigate('/urlaub'); }}
                  className="p-2.5 bg-slate-950/50 rounded-sm border border-slate-800/40 cursor-pointer hover:border-slate-700 transition-colors">
                  <div className="flex items-center gap-2 mb-0.5">
                    {n.priority === 'high' && <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />}
                    {n.priority === 'medium' && <Clock className="w-3.5 h-3.5 text-amber-400" />}
                    <span className="text-xs font-medium text-slate-200">{n.title}</span>
                  </div>
                  <p className="text-[10px] text-slate-500">{n.detail}</p>
                </div>
              ))}
            </div>
          ) : <div className="h-[220px] flex items-center justify-center text-slate-600 text-sm">Keine offenen Benachrichtigungen</div>}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-amber-500" />Heutige Einsätze <Badge className="bg-slate-800 text-slate-400 rounded-sm text-[10px] ml-auto">{stats?.today_assignments?.length || 0}</Badge></h2>
          {stats?.today_assignments?.length ? stats.today_assignments.map(a => (
            <div key={a.id} className="flex items-center justify-between py-2 px-3 bg-slate-950/50 rounded-sm border border-slate-800/40 mb-1">
              <span className="text-sm text-slate-300">{a.notes || 'Einsatz'}</span>
              <span className="text-xs font-mono text-slate-500">{a.start_time} – {a.end_time}</span>
            </div>
          )) : <p className="text-sm text-slate-600">Keine Einsätze für heute</p>}
        </div>
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-amber-500" />Letzte Nachrichten</h2>
          {stats?.recent_messages?.length ? stats.recent_messages.map(m => (
            <div key={m.id} className="py-2 px-3 bg-slate-950/50 rounded-sm border border-slate-800/40 mb-1">
              <div className="flex items-center justify-between mb-0.5"><span className="text-xs font-medium text-amber-400">{m.user_name}</span><span className="text-[10px] font-mono text-slate-600">{new Date(m.created_at).toLocaleString('de-DE')}</span></div>
              <p className="text-sm text-slate-300 truncate">{m.content}</p>
            </div>
          )) : <p className="text-sm text-slate-600">Noch keine Nachrichten</p>}
        </div>
      </div>
    </>
  );
}

function EmployeeDashboard({ API, navigate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/my`).then(r => setData(r.data)).catch(() => {});
  }, [API]);

  return (
    <>
      <h1 className="text-3xl font-bold text-white tracking-tight">Mein Bereich</h1>

      {/* My Today */}
      <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-amber-500" />Meine heutigen Einsätze
          <Badge className="bg-slate-800 text-slate-400 rounded-sm text-[10px] ml-auto">{data?.my_today?.length || 0}</Badge>
        </h2>
        {data?.my_today?.length ? data.my_today.map(a => (
          <div key={a.id} className="flex items-center justify-between py-3 px-4 bg-slate-950/50 rounded-sm border border-slate-800/40 mb-2">
            <div>
              <p className="text-sm font-medium text-white">{a.project_name || 'Projekt'}</p>
              {a.notes && <p className="text-xs text-slate-400">{a.notes}</p>}
            </div>
            <span className="text-sm font-mono text-amber-400">{a.start_time} – {a.end_time}</span>
          </div>
        )) : <p className="text-sm text-slate-500">Keine Einsätze für heute</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* My Projects */}
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <FolderKanban className="w-4 h-4 text-amber-500" />Meine Projekte
            <Badge className="bg-slate-800 text-slate-400 rounded-sm text-[10px] ml-auto">{data?.my_projects?.length || 0}</Badge>
          </h2>
          {data?.my_projects?.length ? data.my_projects.map(p => (
            <div key={p.id} onClick={() => navigate('/projekte')} className="flex items-center justify-between py-2 px-3 bg-slate-950/50 rounded-sm border border-slate-800/40 mb-1 cursor-pointer hover:border-slate-700">
              <div><p className="text-sm font-medium text-white">{p.name}</p><p className="text-xs text-slate-500">{p.description?.slice(0, 50)}</p></div>
              <ArrowRight className="w-4 h-4 text-slate-500" />
            </div>
          )) : <p className="text-sm text-slate-500">Noch keinen Projekten zugeteilt</p>}
        </div>

        {/* My Upcoming */}
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />Kommende Einsätze
          </h2>
          {data?.my_upcoming?.length ? data.my_upcoming.slice(0, 6).map(a => (
            <div key={a.id} className="flex items-center justify-between py-2 px-3 bg-slate-950/50 rounded-sm border border-slate-800/40 mb-1">
              <div><p className="text-sm text-white">{a.project_name}</p><p className="text-[10px] text-slate-500">{a.date?.split('-').reverse().join('.')}</p></div>
              <span className="text-xs font-mono text-slate-400">{a.start_time}–{a.end_time}</span>
            </div>
          )) : <p className="text-sm text-slate-500">Keine kommenden Einsätze</p>}
        </div>
      </div>

      {/* My Messages */}
      <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-amber-500" />Nachrichten aus meinen Projekten
        </h2>
        {data?.my_messages?.length ? data.my_messages.map(m => (
          <div key={m.id} className="py-2 px-3 bg-slate-950/50 rounded-sm border border-slate-800/40 mb-1">
            <div className="flex items-center justify-between mb-0.5"><span className="text-xs font-medium text-amber-400">{m.user_name}</span><span className="text-[10px] font-mono text-slate-600">{new Date(m.created_at).toLocaleString('de-DE')}</span></div>
            <p className="text-sm text-slate-300 truncate">{m.content}</p>
          </div>
        )) : <p className="text-sm text-slate-500">Keine neuen Nachrichten</p>}
      </div>
    </>
  );
}

export default function Dashboard() {
  const { API, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const showAdmin = isAdmin || user?.role === 'projektleiter';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {showAdmin ? <AdminDashboard API={API} navigate={navigate} /> : <EmployeeDashboard API={API} navigate={navigate} />}
    </div>
  );
}
